import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRightIcon, ChevronDownIcon, MenuIcon } from "lucide-react";
import { Logo } from "../ui/Logo";
import { CtaLink } from "../ui/CtaLink";
import { MegaMenu } from "./MegaMenu";
import { MobileMenu } from "./MobileMenu";
import { PRIMARY_NAV, RESOURCE_LINKS } from "../../data/navigation";
type Menu = "services" | "resources" | null;
const linkCls = ({
  isActive
}: {
  isActive: boolean;
}) => `inline-flex min-h-[44px] items-center px-3 text-[15px] transition-colors duration-150 ease-out ${isActive ? "text-fg" : "text-fg-2 hover:text-fg"}`;
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState<Menu>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const {
    pathname
  } = useLocation();
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    setMenu(null);
    setMobileOpen(false);
  }, [pathname]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMenu(null);
    const onClick = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) setMenu(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);
  const solid = scrolled || menu !== null;
  const servicesActive = pathname.startsWith("/services");
  const resourcesActive = pathname.startsWith("/resources");
  return <>
      <header ref={headerRef} onMouseLeave={() => setMenu(null)} className={`sticky top-0 z-50 border-b transition-[background-color,border-color] duration-200 ease-out ${solid ? "border-line bg-ink/75 backdrop-blur-xl" : "border-transparent bg-ink/0"}`} data-id="element-292" data-mp="3acdcjgg2w5yc">
        <div className="shell flex h-[clamp(68px,5vw,84px)] items-center justify-between gap-6" data-id="element-293" data-mp="3ee8cohm9wdaj">
          <Logo data-id="element-294" data-mp="2qo4guwfbthu3" />

          <nav aria-label="Main" className="hidden items-center xl:flex" data-id="element-295" data-mp="2qnmgbnlmshgd">
            <button type="button" aria-expanded={menu === "services"} aria-controls="services-menu" onMouseEnter={() => setMenu("services")} onClick={() => setMenu(menu === "services" ? null : "services")} className={`inline-flex min-h-[44px] items-center gap-1 px-3 text-[15px] transition-colors duration-150 ease-out ${servicesActive || menu === "services" ? "text-fg" : "text-fg-2 hover:text-fg"}`} data-id="element-296" data-mp="2mn2g2b5rgxq0">
              Services
              <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${menu === "services" ? "rotate-180" : ""}`} aria-hidden data-id="element-297" data-mp="igiyhqs4q5gd" />
            </button>
            {PRIMARY_NAV.map(item => <NavLink key={item.href} to={item.href} className={linkCls} onMouseEnter={() => setMenu(null)} data-id="element-298" data-mp="9hdvo89jbpye">
                {item.label}
              </NavLink>)}
            <div className="relative" onMouseEnter={() => setMenu("resources")} data-id="element-299" data-mp="124i64srecwhk">
              <button type="button" aria-expanded={menu === "resources"} aria-controls="resources-menu" onClick={() => setMenu(menu === "resources" ? null : "resources")} className={`inline-flex min-h-[44px] items-center gap-1 px-3 text-[15px] transition-colors duration-150 ease-out ${resourcesActive || menu === "resources" ? "text-fg" : "text-fg-2 hover:text-fg"}`} data-id="element-300" data-mp="1663eq1wweoly">
                Resources
                <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${menu === "resources" ? "rotate-180" : ""}`} aria-hidden data-id="element-301" data-mp="10593nd34npa" />
              </button>
              <AnimatePresence data-id="element-302" data-mp="3scq5s2rnvy66">
                {menu === "resources" && <motion.div id="resources-menu" initial={{
                opacity: 0,
                y: -6,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -6,
                scale: 0.98
              }} transition={{
                duration: 0.16,
                ease: [0.23, 1, 0.32, 1]
              }} className="absolute left-0 top-full w-[300px] origin-top-left pt-2" data-id="element-303" data-mp="3odqyibigjpbk">
                    <ul className="rounded-2xl border border-line bg-ink-2/95 p-2 shadow-2xl backdrop-blur-xl" data-id="element-304" data-mp="5zi3ct6acx1w">
                      {RESOURCE_LINKS.map(r => <li key={r.href} data-id="element-305" data-mp="2vmh3qar9cad5">
                          <Link to={r.href} className="block rounded-xl p-3 transition-colors duration-150 ease-out hover:bg-white/5" data-id="element-306" data-mp="2qoxp39qndvko">
                            <span className="block text-[15px] font-medium" data-id="element-307" data-mp="2gn3j4mnj7629">{r.label}</span>
                            <span className="block text-[13px] text-muted" data-id="element-308" data-mp="2lleu50pkbmm2">{r.blurb}</span>
                          </Link>
                        </li>)}
                    </ul>
                  </motion.div>}
              </AnimatePresence>
            </div>
            <NavLink to="/contact" className={linkCls} onMouseEnter={() => setMenu(null)} data-id="element-309" data-mp="2zb06vtcrr5ln">
              Contact
            </NavLink>
          </nav>

          <div className="flex items-center gap-2" data-id="element-310" data-mp="39a7st4xkk8kd">
            <Link to="/client-login" className="hidden min-h-[44px] items-center px-3 text-[15px] text-fg-2 hover:text-fg md:inline-flex" data-id="element-311" data-mp="39bituwzmr27s">
              Client Login
            </Link>
            <CtaLink to="/contact" track="header_build" className="hidden sm:inline-flex" data-id="element-312" data-mp="1ex86yu2lii4n">
              Build My Website <ArrowUpRightIcon className="h-4 w-4" data-id="element-313" data-mp="1jw2nfpd3bull" />
            </CtaLink>
            <button type="button" onClick={() => setMobileOpen(true)} aria-label="Open menu" aria-expanded={mobileOpen} className="grid h-11 w-11 place-items-center rounded-full border border-line-strong xl:hidden" data-id="element-314" data-mp="1bu21fliw0518">
              <MenuIcon className="h-5 w-5" data-id="element-315" data-mp="1gt54j0fb416k" />
            </button>
          </div>
        </div>

        <AnimatePresence data-id="element-316" data-mp="1usxswp3d25bd">{menu === "services" && <MegaMenu onNavigate={() => setMenu(null)} data-id="element-317" data-mp="1urv5ws95sn0q" />}</AnimatePresence>
      </header>
      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} data-id="element-318" data-mp="1ptl809w1v8bf" />
    </>;
}